import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Stack,
  Button,
  Dialog,
  Divider,
  MenuItem,
  IconButton,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
  FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DialogProps } from '@mui/material/Dialog';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import axios from '@/utils/api/axios';
import { v4 as uuidv4 } from 'uuid';
import { useForm, UseFormSetValue, UseFormGetValues } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import FormProvider, {
  RHFSelect,
  RHFTextField,
  RHFNumberField,
} from '@/components/forms/hook-form';
import * as Yup from 'yup';
import i18n from '@/locales/i18n';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Upload, MultiFilePreview } from '@/components/media/upload';
import { LoadingButton } from '@mui/lab';
import moment from 'moment';
import { OrderDocumentsSection } from '../components/order-documents-section';
import { IOrderDocument, ORDER_DOCUMENT_TYPES } from '@/types/order-document';

interface Props extends DialogProps {
  title?: string;
  onCreate?: VoidFunction;
  onUpdate?: VoidFunction;
  organizationId: string;
  index: number;
  currentDocument?: DocumentInput;
  onChangeFolderName?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  open: boolean;
  onClose: VoidFunction;
  setExternalValue: UseFormSetValue<any>;
  getValues: UseFormGetValues<any>;
  legalEntityId?: string;
  onSave?: (document: IOrderDocument) => IOrderDocument;
  /** Custom form field path for documents. If provided, overrides the default path calculation. */
  documentFieldPath?: string;
}

type DocumentInput = IOrderDocument | null | undefined;

export enum fileStatus {
  pending = 'pending',
  inProgress = 'inProgress',
  complete = 'complete',
}
interface FileWithStatus extends File {
  tmpId: string;
  progress: number;
  status: string;
  preview: string;
  _id?: string;
}

const getFileWithStatus = (_id: string, file: any): FileWithStatus => {
  const rawName = file?.name || 'document.pdf';
  const pdfFileName = rawName.toLowerCase();

  const fileWithStatus = {
    name: pdfFileName,
    type: 'application/pdf',
    size: file.size || 0,
    lastModified: new Date().getTime(),
    path: pdfFileName,
    preview: decodeURI(file.url) || '',
    tmpId: uuidv4(),
    status: fileStatus.pending,
    progress: 100,
    slice: () => new Blob(),
    _id,
    documentId: _id,
  } as unknown as FileWithStatus;

  return fileWithStatus;
};

const isValidDocument = (doc: any): doc is IOrderDocument => {
  return doc && doc._id && typeof doc._id === 'string';
};

// Helper function to filter out empty fields
const filterEmptyFields = (data: any) => {
  const filtered: any = {};

  Object.keys(data).forEach((key) => {
    const value = data[key];
    if (hasValidValue(value)) {
      filtered[key] = value;
    }
  });

  return filtered;
};

// Helper function to check if a field has a valid value
const hasValidValue = (value: any): boolean => {
  return (
    value &&
    value !== null &&
    value !== undefined &&
    (typeof value !== 'string' || value.trim() !== '')
  );
};

// Helper function to append valid fields to FormData
const appendValidFields = (formData: FormData, fields: Record<string, any>) => {
  Object.entries(fields).forEach(([key, value]) => {
    if (hasValidValue(value)) {
      formData.append(key, value);
    }
  });
};

export default function OrderUploadOVDialog({
  title = 'Upload File',
  open,
  onClose,
  onCreate,
  onUpdate,
  organizationId,
  onChangeFolderName,
  setExternalValue,
  getValues,
  index,
  currentDocument,
  legalEntityId,
  onSave,
  documentFieldPath,
  ...other
}: Props) {
  const { t } = useTranslation();

  const [files, setFiles] = useState<FileWithStatus[]>(
    isValidDocument(currentDocument) && currentDocument?.file
      ? [getFileWithStatus(currentDocument._id, currentDocument.file)]
      : []
  );
  const [filesToDelete, setFilesToDelete] = useState<string[]>([]);
  const [editingDocument, setEditingDocument] = useState<any>(null);

  let documentField: string = `legalEntity[${index}].documents`;
  if (documentFieldPath) {
    documentField = documentFieldPath;
  } else if (legalEntityId) {
    documentField = `salesOrders.${legalEntityId}.documents`;
  }

  const checkPurchaseOrderExists = useCallback(() => {
    if (!getValues) return false;
    const existingDocuments = getValues(documentField) || [];
    return existingDocuments.some(
      (doc: any) =>
        doc.documentType === ORDER_DOCUMENT_TYPES.PURCHASE_ORDER &&
        (!editingDocument || doc._id !== editingDocument._id) &&
        (!currentDocument || doc._id !== currentDocument._id)
    );
  }, [getValues, documentField, editingDocument, currentDocument]);

  const NewSchema = useMemo(
    () =>
      Yup.object().shape({
        documentType: Yup.string().required(t('Document Type is required')),
        number: Yup.string().when('documentType', {
          is: (val: string) =>
            val === ORDER_DOCUMENT_TYPES.PURCHASE_ORDER ||
            val === ORDER_DOCUMENT_TYPES.DELIVERY_SHEET,
          then: (schema) => schema.required(t('Number is required')),
          otherwise: (schema) => schema.notRequired(),
        }),
        amount: Yup.string().when('documentType', {
          is: (val: string) => val === ORDER_DOCUMENT_TYPES.PURCHASE_ORDER,
          then: (schema) => schema.required(t('Amount is required')),
          otherwise: (schema) => schema.notRequired(),
        }),
        files:
          editingDocument || isValidDocument(currentDocument)
            ? Yup.array().of(Yup.mixed()).notRequired()
            : Yup.array().of(Yup.mixed()).min(1, t('At least one file is required')),
        documentDate: Yup.string().when('documentType', {
          is: (val: string) =>
            val === ORDER_DOCUMENT_TYPES.PURCHASE_ORDER ||
            val === ORDER_DOCUMENT_TYPES.DELIVERY_SHEET,
          then: (schema) => schema.required(t('Date is required')),
          otherwise: (schema) => schema.notRequired(),
        }),
        description: Yup.string().when('documentType', {
          is: (val: string) => val === ORDER_DOCUMENT_TYPES.OTHER,
          then: (schema) =>
            schema
              .required(t('Description is required'))
              .max(50, t('Description must be at most 50 characters')),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    [t, editingDocument, currentDocument]
  );

  const defaultValues = useMemo(() => {
    const docToEdit = editingDocument || currentDocument;

    if (isValidDocument(docToEdit)) {
      const newValues: {
        documentType: string;
        number: string;
        amount: string;
        documentDate: string | Date;
        description: string;
        files?: FileWithStatus[];
      } = {
        documentType: docToEdit.documentType || ORDER_DOCUMENT_TYPES.PURCHASE_ORDER,
        number: docToEdit.number ? docToEdit.number.toString() : '',
        amount: docToEdit.amount ? docToEdit.amount.toString() : '',
        documentDate: docToEdit.documentDate || '',
        description: docToEdit.description || '',
      };

      return newValues;
    }

    const existingDocuments = getValues ? getValues(documentField) || [] : [];
    const hasPurchaseOrder = existingDocuments.some(
      (doc: IOrderDocument) => doc.documentType === ORDER_DOCUMENT_TYPES.PURCHASE_ORDER
    );

    const defaultDocumentType = hasPurchaseOrder
      ? ORDER_DOCUMENT_TYPES.DELIVERY_SHEET
      : ORDER_DOCUMENT_TYPES.PURCHASE_ORDER;

    return {
      documentType: defaultDocumentType,
      number: '',
      amount: '',
      documentDate: '',
      description: '',
      files: [],
    };
  }, [currentDocument, editingDocument, getValues, documentField]);

  const methods = useForm({
    mode: 'all',
    resolver: yupResolver<any>(NewSchema),
    defaultValues,
  });

  const {
    reset,
    setValue,
    watch,
    handleSubmit,
    setError,
    formState: { isSubmitting, errors },
  } = methods;

  const values = watch();
  const { mutate, isLoading } = useMutation({
    mutationFn: async (data: any) => {
      if (filesToDelete.length > 0) {
        await Promise.all(
          filesToDelete.map(async (docId) => {
            try {
              await axios.delete(`/order-documents/${docId}`);
            } catch (err) {
              Sentry.captureException(err);
            }
          })
        );
      }

      const documentToUpdate = editingDocument || currentDocument;

      if (isValidDocument(documentToUpdate)) {
        if (files.length === 0 || files[0]._id === documentToUpdate._id) {
          const updateData = filterEmptyFields({
            documentType: data.documentType,
            amount: data.amount,
            number: data.number,
            documentDate: data.documentDate,
            description: data.description,
          });

          return axios.patch(`order-documents/${documentToUpdate._id}`, updateData);
        }
      }

      const formData = new FormData();
      if (files.length > 0 && !files[0]._id) {
        formData.append('file', files[0]);
      }
      formData.append('organization', organizationId);
      formData.append('documentType', data.documentType);

      appendValidFields(formData, {
        amount: data.amount,
        number: data.number,
        documentDate: data.documentDate,
        description: data.description,
      });

      if (isValidDocument(documentToUpdate)) {
        return axios.patch(`order-documents/${documentToUpdate._id}`, formData);
      }

      return axios.post(`order-documents`, formData);
    },
    onSuccess: (response) => {
      let updatedData = response.data;
      const documentToUpdate = editingDocument || currentDocument;

      if (response.data.id && !response.data.file && isValidDocument(documentToUpdate)) {
        updatedData = {
          ...documentToUpdate,
          _id: response.data.id || documentToUpdate._id,
          documentType: values.documentType || documentToUpdate.documentType,
          amount: values.amount || documentToUpdate.amount,
          number: values.number || documentToUpdate.number,
          documentDate: values.documentDate || documentToUpdate.documentDate,
          description: values.description || documentToUpdate.description,
          updatedAt: new Date().toISOString(),
        };
      }

      if (files.length > 0) {
        updatedData.originalBlob = files[0].preview;
        updatedData.file = {
          ...updatedData.file,
          name: files[0].name,
          url: files[0].preview || updatedData.file?.url,
          size: files[0].size || updatedData.file?.size || 0,
          type: files[0].type || updatedData.file?.type || 'application/pdf',
        };

        if (files[0].preview && !files[0]._id) {
          updatedData.file.url = files[0].preview;
        }
      }

      if (!updatedData.updatedAt) {
        updatedData.updatedAt = new Date().toISOString();
      }

      const existingDocuments = getValues(documentField) || [];
      let updatedDocuments;

      // Edit an existing document
      if (
        editingDocument &&
        existingDocuments.some((doc: IOrderDocument) => doc._id === editingDocument._id)
      ) {
        updatedDocuments = existingDocuments.map((doc: IOrderDocument) =>
          doc._id === editingDocument._id ? { ...updatedData } : doc
        );
      }
      // Edit the current document
      else if (
        isValidDocument(documentToUpdate) &&
        existingDocuments.some((doc: IOrderDocument) => doc._id === documentToUpdate._id)
      ) {
        updatedDocuments = existingDocuments.map((doc: IOrderDocument) =>
          doc._id === documentToUpdate._id ? { ...updatedData } : doc
        );
      }
      // it's a new document
      else {
        const documentExists = existingDocuments.some(
          (doc: IOrderDocument) => doc._id === updatedData._id
        );
        updatedDocuments = documentExists ? existingDocuments : [...existingDocuments, updatedData];
      }

      setExternalValue(documentField, updatedDocuments);

      if (onSave) {
        onSave(updatedData);
      }

      clearForm();
    },
    onError: (e) => {
      // Upload error handled
    },
  });

  const onSubmit = handleSubmit((data) => {
    try {
      if (data.documentType === ORDER_DOCUMENT_TYPES.PURCHASE_ORDER && checkPurchaseOrderExists()) {
        setError('documentType', {
          type: 'manual',
          message: t('Only one Purchase Order document is allowed'),
        });
        return;
      }

      mutate(data);
    } catch (error) {
      Sentry.captureException(error);
    }
  });

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const newFiles = acceptedFiles.map((file) => {
        const preview = URL.createObjectURL(file);
        return Object.assign(file, {
          tmpId: uuidv4(),
          status: fileStatus.pending,
          progress: 0,
          preview,
        });
      });
      setValue('files', newFiles);
      setFiles(newFiles);
    },
    [setValue]
  );

  const handleRemoveFile = (inputFile: File | string) => {
    const { _id, documentId: fileDocumentId } = inputFile as any;

    if (_id || fileDocumentId) {
      const docId = _id || fileDocumentId;
      if (docId) {
        setFilesToDelete([...filesToDelete, docId]);
      }
    } else {
      if (
        currentDocument &&
        isValidDocument(currentDocument) &&
        (inputFile as any).name === currentDocument.file?.name
      ) {
        setFilesToDelete([...filesToDelete, currentDocument._id]);
      }
    }

    const filtered = files.filter((file) => file !== inputFile);
    setValue('files', filtered);
    setFiles(filtered);
  };

  const getNumberLabel = () => {
    if (values.documentType === ORDER_DOCUMENT_TYPES.PURCHASE_ORDER) {
      return t('OC Number');
    } else if (values.documentType === ORDER_DOCUMENT_TYPES.DELIVERY_SHEET) {
      return t('Sheet Number (HES)');
    }
    return t('Number');
  };

  const getDateLabel = () => {
    if (values.documentType === ORDER_DOCUMENT_TYPES.PURCHASE_ORDER) {
      return t('OC Date');
    } else if (values.documentType === ORDER_DOCUMENT_TYPES.DELIVERY_SHEET) {
      return t('Sheet Date');
    }
    return t('Date');
  };

  const getAmountLabel = () => {
    return t('OC Amount (Net Amount)');
  };

  const getDescriptionLabel = () => {
    if (values.documentType === ORDER_DOCUMENT_TYPES.OTHER) {
      return t('What type of document is it?');
    }
    return t('Comments');
  };

  const shouldShowField = (fieldName: string) => {
    const docType = values.documentType;

    switch (fieldName) {
      case 'number':
      case 'documentDate':
        return (
          docType === ORDER_DOCUMENT_TYPES.PURCHASE_ORDER ||
          docType === ORDER_DOCUMENT_TYPES.DELIVERY_SHEET
        );
      case 'amount':
        return docType === ORDER_DOCUMENT_TYPES.PURCHASE_ORDER;
      case 'description':
        return (
          docType === ORDER_DOCUMENT_TYPES.EMAIL_APPROVAL || docType === ORDER_DOCUMENT_TYPES.OTHER
        );
      default:
        return true;
    }
  };

  const clearForm = useCallback(() => {
    reset();
    setFiles([]);
    setEditingDocument(null);
    setFilesToDelete([]);

    // After clearing, check if we should set a different default document type
    const existingDocuments = getValues ? getValues(documentField) || [] : [];
    const hasPurchaseOrder = existingDocuments.some(
      (doc: IOrderDocument) => doc.documentType === ORDER_DOCUMENT_TYPES.PURCHASE_ORDER
    );

    if (hasPurchaseOrder) {
      setValue('documentType', ORDER_DOCUMENT_TYPES.DELIVERY_SHEET);
    }
  }, [reset, getValues, documentField, setValue]);

  const startEditingDocument = useCallback(
    (doc: IOrderDocument) => {
      setEditingDocument(doc);

      setValue('documentType', doc.documentType || ORDER_DOCUMENT_TYPES.PURCHASE_ORDER);
      setValue('number', doc.number?.toString() || '');
      setValue('amount', doc.amount?.toString() || '');
      setValue('documentDate', doc.documentDate || '');
      setValue('description', doc.description || '');

      if (doc.file) {
        const fileWithStatus = getFileWithStatus(doc._id, doc.file);
        setFiles([fileWithStatus]);
        setValue('files', [fileWithStatus]);
      } else {
        setFiles([]);
        setValue('files', []);
      }
    },
    [setValue]
  );

  const handleEditExistingDocument = useCallback(
    (doc: IOrderDocument) => {
      startEditingDocument(doc);
    },
    [startEditingDocument]
  );

  const handleDeleteExistingDocument = useCallback(
    (doc: IOrderDocument) => {
      const existingDocuments = getValues(documentField) || [];
      const docIndex = existingDocuments.findIndex(
        (document: IOrderDocument) => document._id === doc._id
      );

      if (docIndex !== -1) {
        const updatedDocuments = existingDocuments.filter(
          (_: IOrderDocument, i: number) => i !== docIndex
        );
        setExternalValue(documentField, updatedDocuments);

        if (doc._id) {
          setFilesToDelete([...filesToDelete, doc._id]);
        }
      }
    },
    [getValues, setExternalValue, documentField, filesToDelete, setFilesToDelete]
  );

  useEffect(() => {
    if (open && !isValidDocument(currentDocument)) {
      clearForm();
    }
  }, [open, currentDocument, clearForm]);

  // Effect to automatically change document type after creating a Purchase Order
  useEffect(() => {
    if (open && !editingDocument && !currentDocument) {
      const existingDocuments = getValues ? getValues(documentField) || [] : [];
      const hasPurchaseOrder = existingDocuments.some(
        (doc: IOrderDocument) => doc.documentType === ORDER_DOCUMENT_TYPES.PURCHASE_ORDER
      );

      const currentDocType = watch('documentType');

      if (hasPurchaseOrder && currentDocType === ORDER_DOCUMENT_TYPES.PURCHASE_ORDER) {
        setValue('documentType', ORDER_DOCUMENT_TYPES.DELIVERY_SHEET);
      }
    }
  }, [open, editingDocument, currentDocument, getValues, documentField, setValue, watch]);

  const sectionsToHide = { AddButton: true, EditButton: false };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={() => {
        setFilesToDelete([]);
        onClose();
      }}
      {...other}
    >
      <DialogTitle variant="h4" sx={{ px: 4, pb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pb: 1 }}>
          <Box>{t('Document Management')}</Box>
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {t('Add and manage documents related to this purchase order.')}
        </Typography>
      </DialogTitle>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent dividers sx={{ pt: 1, pb: 0, border: 'none' }}>
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">
                {editingDocument ? t('Edit Document') : t('Add New Document')}
              </Typography>
              {editingDocument && (
                <Button size="small" onClick={clearForm} startIcon={<CloseIcon />}>
                  {t('Cancel Edit')}
                </Button>
              )}
            </Stack>
            {editingDocument && (
              <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                {t('Editing document:')}{' '}
                {editingDocument.documentType === ORDER_DOCUMENT_TYPES.PURCHASE_ORDER
                  ? t('Purchase Order')
                  : editingDocument.documentType === ORDER_DOCUMENT_TYPES.DELIVERY_SHEET
                    ? t('Delivery Sheet')
                    : editingDocument.documentType === ORDER_DOCUMENT_TYPES.EMAIL_APPROVAL
                      ? t('Email Approval')
                      : t('Other')}
                {editingDocument.number && ` - ${editingDocument.number}`}
              </Typography>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <RHFSelect name="documentType" label={t('Document Type')} size="small">
              <MenuItem
                value={ORDER_DOCUMENT_TYPES.PURCHASE_ORDER}
                disabled={checkPurchaseOrderExists()}
              >
                {t('Purchase Order')}
                {checkPurchaseOrderExists() && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    ({t('Already exists')})
                  </Typography>
                )}
              </MenuItem>
              <MenuItem value={ORDER_DOCUMENT_TYPES.DELIVERY_SHEET}>
                {t('Delivery Sheet (HES)')}
              </MenuItem>
              <MenuItem value={ORDER_DOCUMENT_TYPES.EMAIL_APPROVAL}>{t('Email Approval')}</MenuItem>
              <MenuItem value={ORDER_DOCUMENT_TYPES.OTHER}>{t('Other')}</MenuItem>
            </RHFSelect>
            {checkPurchaseOrderExists() && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                {t(
                  'Note: Only one Purchase Order document is allowed. You can edit the existing one or choose a different document type.'
                )}
              </Typography>
            )}
          </Box>

          <Box
            rowGap={3}
            columnGap={1}
            display="grid"
            sx={{
              mb: 2,
            }}
            gridTemplateColumns={{
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(2, 1fr)',
            }}
          >
            {shouldShowField('number') && (
              <RHFTextField name="number" label={getNumberLabel()} sx={{ mb: 1 }} />
            )}
            {shouldShowField('documentDate') && (
              <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={i18n.language}>
                <DatePicker
                  label={getDateLabel()}
                  value={values.documentDate?.length ? moment(values.documentDate) : null}
                  onChange={(newValue) => {
                    setValue(
                      'documentDate',
                      moment(newValue).isValid() ? newValue?.toISOString() : '',
                      { shouldValidate: true }
                    );
                  }}
                  slotProps={{
                    textField: {
                      error: !!errors.documentDate,
                      helperText: errors.documentDate?.message as string,
                      size: 'small',
                    },
                  }}
                />
                {'  '}
              </LocalizationProvider>
            )}
            {shouldShowField('amount') && (
              <RHFNumberField
                currency
                allowNegative={false}
                decimalScale={4}
                name="amount"
                prefix="$ "
                label={getAmountLabel()}
              />
            )}
          </Box>

          {shouldShowField('description') && (
            <Box sx={{ mb: 2 }}>
              <RHFTextField
                name="description"
                label={getDescriptionLabel()}
                multiline
                rows={2}
                placeholder={
                  values.documentType === ORDER_DOCUMENT_TYPES.OTHER
                    ? t('Please specify the document type (max 50 characters)')
                    : t('Add your comments here')
                }
                inputProps={
                  values.documentType === ORDER_DOCUMENT_TYPES.OTHER ? { maxLength: 50 } : {}
                }
              />
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {editingDocument ? t('Update File (Optional)') : t('Attach File')}
            </Typography>
            {editingDocument && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {t('Leave empty to keep the current file, or upload a new one to replace it.')}
              </Typography>
            )}
          </Box>

          {!files.length && (
            <>
              <FormHelperText error={!!errors.files} sx={{ p: 2 }}>
                {errors.files?.message as any}
              </FormHelperText>
              <Upload files={files} onDrop={handleDrop} onRemove={handleRemoveFile} />
            </>
          )}
          {files.length > 0 && (
            <Box sx={{ my: 3 }}>
              <MultiFilePreview
                files={files}
                onRemove={handleRemoveFile}
                onRemoveWithId={handleRemoveFile}
                showPdfPreview
              />
            </Box>
          )}

          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 3 }} />
            <OrderDocumentsSection
              documents={getValues(documentField) || []}
              onEditDocument={handleEditExistingDocument}
              onDeleteDocument={handleDeleteExistingDocument}
              sectionsToHide={sectionsToHide}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setFilesToDelete([]);
              clearForm();
              onClose();
            }}
          >
            {t('Close')}
          </Button>
          <LoadingButton
            color="primary"
            type="submit"
            sx={{ mr: 1 }}
            variant="contained"
            loading={isSubmitting || isLoading}
          >
            {editingDocument ? t('Update Document') : t('Save Document')}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
